/* eslint-disable no-console */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { EMPTY, Observable, from, of, timer } from 'rxjs';
import { catchError, delay, switchMap, tap } from 'rxjs/operators';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { SwPush, SwUpdate, VersionEvent } from '@angular/service-worker';
import { environment } from '../../../environments/environment';
import { CustomHeader } from '../models/header.enum';
import { msgType, SnackData } from '../ui/sidenav/snack/snack-data.model';
import { SnackComponent } from '../ui/sidenav/snack/snack.component';

@Injectable({
    providedIn: 'root',
})

export class NotificationService {
    public busy: WritableSignal<boolean> = signal(false);
    private readonly http = inject(HttpClient);
    private readonly snack = inject(MatSnackBar);
    private readonly swUpdate = inject(SwUpdate);
    private readonly swPush = inject(SwPush);


    getAll(): Observable<Notification[]> {
        return this.http.get<Notification[]>(`${environment.urls.api}notifications`);
    }


    getCount(userId: number): Observable<number> {
        return timer(0, 30e3)
            .pipe(
                delay(30e3),
                switchMap(() => {
                    return this.http.get<number>(
                        `${environment.urls.api}notification/count/${userId}`,
                        { headers: { [CustomHeader.SkipLoadingHeader]: '' } }
                    ).pipe(
                        tap(t => this.setBadge(t)),
                        catchError(() => EMPTY),
                    );
                })
            );
    }


    close(notificationId: number): Observable<void> {
        return this.http.put<void>(`${environment.urls.api}notification`, notificationId);
    }


    showMsg(msg: string, type: msgType = 'info', duration = 5000): MatSnackBarRef<SnackComponent> {
        const data: SnackData = { msg, type };
        if (type === 'error') {
            this.vibrate();
        }
        return this.snack.openFromComponent(SnackComponent, { duration, data });
    }


    checkForUpdates(): Observable<VersionEvent> {
        return this.swUpdate.isEnabled ?
            this.swUpdate.versionUpdates.pipe(
                tap(sw => {

                    switch (sw.type) {
                        case 'VERSION_DETECTED':
                            console.info(`Downloading new app version: ${sw.version.hash}`);
                            this.showMsg(`Pobieranie nowej wersji...`);
                            break;

                        case 'VERSION_READY':
                            console.info(`New app version ready for use: ${sw.latestVersion.hash}`);
                            this.showMsg(`Instalowanie nowej wersji...`).afterDismissed().subscribe(
                                () => {
                                    const appData = sw.latestVersion.appData || {};
                                    if ('showWhatsNew' in appData && appData.showWhatsNew) {
                                        window.location.href = '/help/1'; // reload to activate changes and show "What's new..."" page 
                                    } else {
                                        window.location.reload(); // just trload page to activate changes
                                    }
                                }
                            );
                            break;

                        case 'VERSION_INSTALLATION_FAILED':
                            console.error(`Failed to install app version: ${sw.error}`);
                            this.showMsg(`Nie udało się zainstalować nowej wersji aplikacji`, 'error');
                            break;
                    }
                })
            ) : EMPTY;
    }


    vibrate(time = 100): void {
        if ('vibrate' in navigator && window.navigator.userActivation.isActive) {
            navigator.vibrate(time);
        }
    }


    setBadge(badge: number): void {
        if ("setAppBadge" in navigator) {
            window.navigator.setAppBadge(badge);
        }
    }


    getNotificationsState(): Observable<PermissionState> {
        return ("permissions" in navigator) ?
            from(
                navigator.permissions.query({ name: "notifications" })
                    .then(result => result.state)
            )
            : of('denied');
    }


    requestNotifications(): Observable<void> {
        return this.getNotificationsState().pipe(
            delay(30e3),
            switchMap(state => {
                return this.swPush.isEnabled && state !== 'denied' ?
                    from(this.swPush.requestSubscription({
                        serverPublicKey: environment.push.publicKey
                    })).pipe(
                        switchMap(sub => this.http.post<void>(`${environment.urls.api}push`, sub.toJSON(), { headers: { [CustomHeader.SkipNotificationHeader]: '' } })
                        ))
                    : EMPTY;
            })
        );
    }
}
